import clsx from "clsx";
import { FunctionComponent } from "react";

import DangerouslySetCMSMarkup from "@ui/components/article/DangerouslySetCMSMarkup";
import {
  type ContentCardArticle,
  type ContentCardBaseConfig,
} from "@ui/components/content-cards/content-cards.types";
import MediaIcon from "@ui/components/media/MediaIcon";
import DuetLink from "@ui/components/navigation/DuetLink";
import { useAnalyticsEventMetadataContext } from "@ui/context/analytics-event-metadata-context";
import { useDataAttributes } from "@ui/hooks/use-data-attributes";
import { sharedStyles } from "@ui/shared-styles";
import { getContentCardPosition } from "@ui/utils/get-content-card-position";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { getPageType } from "@voxmedia/duet-utils/analytics";

import ContentBlockMeta from "./components/ContentBlockMeta/ContentBlockMeta";
import * as styles from "./ContentBlock.css";

export interface Props extends ContentCardArticle, ContentCardBaseConfig {
  componentContext?: string;
  gaComponentName: string;
  className?: string;
  innerClassName?: string;
  headlineContainerClassName?: string;
  headlineClassName?: string;
  dekClassName?: string;
  upperMetaCategoryClassName?: string;
  upperMetaClassName?: string;
  lowerMetaClassName?: string;
}

const getMetaBaseProps = (props: Props) => {
  const {
    adFlag,
    advertiserName,
    advertiserPreamble,
    authors,
    authorByLine,
    category,
    chorusId,
    chorusUuid,
    componentContext,
    commentCount,
    commentCountId,
    commentUrl,
    contentBlockVariation = "standard",
    contentType,
    crossPostSource,
    globalId,
    headline,
    id,
    linkClassName,
    live,
    liveBadgeName,
    metaVariation,
    packageName,
    permalink,
    showAuthorBy,
    site,
    streamName,
    timestamp,
  } = props;

  return {
    adFlag,
    advertiserName,
    advertiserPreamble,
    authorByLine,
    authors,
    category,
    chorusId,
    chorusUuid,
    componentContext,
    commentCount,
    commentCountId,
    commentUrl,
    contentType,
    crossPostSource,
    globalId,
    headline,
    id,
    linkClassName,
    live,
    liveBadgeName,
    metaVariation: metaVariation || contentBlockVariation,
    packageName,
    permalink,
    showAuthorBy,
    site,
    streamName,
    timestamp,
  };
};

export const UpperMeta: FunctionComponent<Props> = (props) => {
  const {
    componentContext,
    contentBlockSize = "medium",
    live,
    upperMetaCategoryClassName,
    upperMetaFirstComponent,
    upperMetaSecondComponent,
    upperMetaThirdComponent,
  } = props;
  return (
    ((upperMetaFirstComponent === "LiveBadge" && live) ||
      (upperMetaFirstComponent && upperMetaFirstComponent !== "LiveBadge") ||
      upperMetaSecondComponent ||
      upperMetaThirdComponent) && (
      <ContentBlockMeta
        {...getMetaBaseProps(props)}
        layout="upper"
        categoryClassName={upperMetaCategoryClassName}
        firstComponent={upperMetaFirstComponent}
        secondComponent={upperMetaSecondComponent}
        thirdComponent={upperMetaThirdComponent}
        contentBlockSize={contentBlockSize}
        componentContext={componentContext}
      />
    )
  );
};

const ContentBlock: FunctionComponent<Props> = (props) => {
  const {
    adFlag,
    authors,
    authorByLine,
    className,
    componentContext,
    contentBlockSize = "medium",
    contentBlockVariation = "standard",
    contentType,
    coverLink,
    dek,
    gaComponentName,
    headline,
    headlineClassName,
    headlineContainerClassName,
    dekClassName,
    innerClassName,
    linkClassName,
    lowerMetaFirstComponent,
    lowerMetaSecondComponent,
    lowerMetaThirdComponent,
    mediaVariation,
    mediaVariationIconPlacement,
    permalink,
    postTemplate,
    showDek,
    site,
    upperMetaClassName,
    lowerMetaClassName,
  } = props;
  const analytics = useAnalytics();

  const { metadata: analyticsMetadata } = useAnalyticsEventMetadataContext();

  return (
    <div {...useDataAttributes(props)} className={className}>
      <div className={innerClassName}>
        <div className={clsx(upperMetaClassName)}>
          <UpperMeta {...props} />
        </div>
        <>
          <div className={headlineContainerClassName}>
            <div
              className={clsx(
                sharedStyles.text.hangingPunctuation,
                adFlag
                  ? styles.headlineBranded
                  : contentBlockVariation === "special"
                    ? styles.headlineSpecial[contentBlockSize]
                    : styles.headlineStandard[contentBlockSize],
                headlineClassName,
              )}
            >
              <DuetLink
                href={permalink}
                hrefSiteData={site}
                className={clsx(
                  sharedStyles.contentCard.contentCardLink,
                  coverLink && sharedStyles.contentCard.coverLink,
                  contentBlockVariation === "special" &&
                    sharedStyles.contentCard.specialContentCardLink,
                  linkClassName,
                )}
                onClick={(e) => {
                  analytics?.emit({
                    event: "article_click",
                    article_click: {
                      article_component_name: gaComponentName,
                      article_uri: permalink,
                      article_title: headline,
                      author: authors[0]?.name ?? authorByLine,
                      page_type: getPageType(contentType, !!postTemplate),
                      position: getContentCardPosition(e.currentTarget),
                      ...analyticsMetadata,
                    },
                  });
                }}
              >
                {headline}
                {mediaVariation && mediaVariationIconPlacement === "title" && (
                  <span className={styles.inlineHeadlineIconContainer}>
                    &#65279;
                    {<MediaIcon placement="title" variation={mediaVariation} />}
                  </span>
                )}
              </DuetLink>
            </div>
          </div>
          {showDek && dek && (
            <DangerouslySetCMSMarkup
              html={dek}
              className={clsx(
                adFlag
                  ? styles.dekBranded
                  : contentBlockVariation === "special"
                    ? styles.dekSpecial[contentBlockSize]
                    : styles.dekStandard[contentBlockSize],
                dekClassName,
              )}
            />
          )}
          {(lowerMetaFirstComponent ||
            lowerMetaSecondComponent ||
            lowerMetaThirdComponent) && (
            <ContentBlockMeta
              {...getMetaBaseProps(props)}
              layout="lower"
              firstComponent={lowerMetaFirstComponent}
              secondComponent={lowerMetaSecondComponent}
              thirdComponent={lowerMetaThirdComponent}
              contentBlockSize={contentBlockSize}
              componentContext={componentContext}
              containerClassName={lowerMetaClassName}
            />
          )}
        </>
      </div>
    </div>
  );
};

export default ContentBlock;
